import type { GeneratedInterpretation, GeneratedNatalReport } from './interpretation.js';
import { buildResultSummary, type ResultSummaryItem } from './resultSummary.js';

export interface OverviewDigestItem {
  topic: ResultSummaryItem['topic'];
  claimKey: string;
  conclusion: string;
  strengthLabel: string;
  evidenceSufficiencyLabel: string;
  sourceIds: readonly string[];
  ruleIds: readonly string[];
}

export interface OverviewDigestViewModel {
  model: 'OVERVIEW-DIGEST-0.1.0';
  status: 'available' | 'insufficient_evidence';
  items: readonly OverviewDigestItem[];
  disclosure: string;
  emptyState?: string;
}

export function buildOverviewDigestFromTopics(topics: readonly GeneratedInterpretation[], limit = 3): OverviewDigestViewModel {
  if (!Number.isInteger(limit) || limit < 1 || limit > 5) {
    throw new RangeError('Overview digest limit must be an integer between 1 and 5');
  }
  const sourceTopics = topics.filter((topic) => topic.topic !== 'overview' && topic.topic !== 'timing');
  const summary = buildResultSummary(sourceTopics, limit);
  const items = summary.items.map((item): OverviewDigestItem => Object.freeze({
    topic: item.topic,
    claimKey: item.claim.claimKey,
    conclusion: item.claim.conclusion,
    strengthLabel: item.claim.strength.label,
    evidenceSufficiencyLabel: item.claim.evidenceSufficiency.label,
    sourceIds: item.claim.sourceIds,
    ruleIds: item.claim.ruleIds,
  }));
  const status = items.length > 0 ? 'available' : 'insufficient_evidence';
  return Object.freeze({
    model: 'OVERVIEW-DIGEST-0.1.0',
    status,
    items: Object.freeze(items),
    disclosure: '命格總覽只整理其他主題已觸發、可追溯的 production Claims，不建立額外的總命格分數、準確率或無來源結論。',
    ...(status === 'insufficient_evidence'
      ? { emptyState: '目前可追溯 Evidence 不足以形成命格總覽；系統保持空白，不使用 AI 或罐頭文字補足。' }
      : {}),
  });
}

export function buildOverviewDigest(report: GeneratedNatalReport, limit = 3): OverviewDigestViewModel {
  return buildOverviewDigestFromTopics(report.topics, limit);
}
